/* tslint:disable:no-bitwise */
import { WebMercatorProjection } from "../projection";
import { Ellipsoid } from "../Ellipsoid";
import { Vector2, Vector3 } from "three";
import { BaseTilingScheme } from "./BaseTilingScheme";
import { Rectangle } from "../Rectangle";
/**
 * A tiling scheme for data references to the WebMercator projection (EPSG:3857).
 *
 * @see TilingScheme
 */
export class WebMercatorTilingScheme extends BaseTilingScheme {
    constructor(options) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        // Resolve option defaults
        const ellipsoid = (_a = options === null || options === void 0 ? void 0 : options.ellipsoid) !== null && _a !== void 0 ? _a : Ellipsoid.WGS_84;
        const numRootTilesX = (_b = options === null || options === void 0 ? void 0 : options.numRootTilesX) !== null && _b !== void 0 ? _b : 1;
        const numRootTilesY = (_c = options === null || options === void 0 ? void 0 : options.numRootTilesY) !== null && _c !== void 0 ? _c : 1;
        const minX = (_e = (_d = options === null || options === void 0 ? void 0 : options.area) === null || _d === void 0 ? void 0 : _d.min.x) !== null && _e !== void 0 ? _e : ellipsoid.maximumRadius * -Math.PI;
        const minY = (_g = (_f = options === null || options === void 0 ? void 0 : options.area) === null || _f === void 0 ? void 0 : _f.min.y) !== null && _g !== void 0 ? _g : ellipsoid.maximumRadius * -Math.PI;
        const maxX = (_j = (_h = options === null || options === void 0 ? void 0 : options.area) === null || _h === void 0 ? void 0 : _h.max.x) !== null && _j !== void 0 ? _j : ellipsoid.maximumRadius * Math.PI;
        const maxY = (_l = (_k = options === null || options === void 0 ? void 0 : options.area) === null || _k === void 0 ? void 0 : _k.max.y) !== null && _l !== void 0 ? _l : ellipsoid.maximumRadius * Math.PI;
        super(numRootTilesX, numRootTilesY);
        this._projection = new WebMercatorProjection(ellipsoid);
        this._nativeSouthWest = new Vector3(minX, minY, 0);
        this._nativeNorthEast = new Vector3(maxX, maxY, 0);
        const southwest = this._projection.unproject(this._nativeSouthWest);
        const northeast = this._projection.unproject(this._nativeNorthEast);
        this._rectangle = new Rectangle(southwest.longitude, southwest.latitude, northeast.longitude, northeast.latitude);
    }
    /**
     * @see TilingScheme.ellipsoid
     */
    get ellipsoid() {
        return this._projection.ellipsoid;
    }
    /**
     * @see TilingScheme.rectangle
     */
    get rectangle() {
        return this._rectangle;
    }
    /**
     * @see TilingScheme.projection
     */
    get projection() {
        return this._projection;
    }
    /**
     * @see TilingScheme.rectangleToNativeRectangle
     */
    rectangleToNativeRectangle(rectangle, result = new Rectangle()) {
        const southwest = this._projection.project(rectangle.southwest);
        const northeast = this._projection.project(rectangle.northeast);
        return result.set(southwest.x, southwest.y, northeast.x, northeast.y);
    }
    /**
     * @see TilingScheme.tileXYToNativeRectangle
     */
    tileXYToNativeRectangle(x, y, level, result = new Rectangle()) {
        const xTiles = this.getNumberOfXTilesAtLevel(level);
        const yTiles = this.getNumberOfYTilesAtLevel(level);
        const overallWidth = this._nativeNorthEast.x - this._nativeSouthWest.x;
        const xTileWidth = overallWidth / xTiles;
        const overallHeight = this._nativeNorthEast.y - this._nativeSouthWest.y;
        const yTileHeight = overallHeight / yTiles;
        const west = this._nativeSouthWest.x + x * xTileWidth;
        const east = this._nativeSouthWest.x + (x + 1) * xTileWidth;
        const north = this._nativeNorthEast.y - y * yTileHeight;
        const south = this._nativeNorthEast.y - (y + 1) * yTileHeight;
        return result.set(west, south, east, north);
    }
    /**
     * @see TilingScheme.tileXYToRectangle
     */
    tileXYToRectangle(x, y, level, result = new Rectangle()) {
        const nativeRectangle = this.tileXYToNativeRectangle(x, y, level, result);
        const southwest = this._projection.unproject(new Vector3(nativeRectangle.west, nativeRectangle.south));
        const northeast = this._projection.unproject(new Vector3(nativeRectangle.east, nativeRectangle.north));
        nativeRectangle.west = southwest.longitude;
        nativeRectangle.south = southwest.latitude;
        nativeRectangle.east = northeast.longitude;
        nativeRectangle.north = northeast.latitude;
        return nativeRectangle;
    }
    /**
     * @see TilingScheme.positionToTileXY
     */
    positionToTileXY(position, level, result = new Vector2()) {
        if (!this._rectangle.contains(position)) {
            throw new Error("Trying to sample tiling scheme outside of it's relevant bounds");
        }
        const xTiles = this.getNumberOfXTilesAtLevel(level);
        const yTiles = this.getNumberOfYTilesAtLevel(level);
        const overallWidth = this._nativeNorthEast.x - this._nativeSouthWest.x;
        const xTileWidth = overallWidth / xTiles;
        const overallHeight = this._nativeNorthEast.y - this._nativeSouthWest.y;
        const yTileHeight = overallHeight / yTiles;
        const webMercatorPosition = this._projection.project(position);
        const distanceFromWest = webMercatorPosition.x - this._nativeSouthWest.x;
        const distanceFromNorth = this._nativeNorthEast.y - webMercatorPosition.y;
        let xTileCoordinate = (distanceFromWest / xTileWidth) | 0;
        if (xTileCoordinate >= xTiles) {
            xTileCoordinate = xTiles - 1;
        }
        let yTileCoordinate = (distanceFromNorth / yTileHeight) | 0;
        if (yTileCoordinate >= yTiles) {
            yTileCoordinate = yTiles - 1;
        }
        return result.set(xTileCoordinate, yTileCoordinate);
    }
}
